import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { dsrApi, processingApi, organizationApi, openPdfExport } from '../services/api';
import {
  DataSubjectRequest,
  DataSubjectRequestType,
  DataSubjectRequestStatus,
  DataSubjectRequestChannel,
  IdentityVerificationStatus,
  Organization,
  ProcessingActivity,
  ProcessingStatus,
  RiskLevel
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  TabContainer,
  Tab,
  MessageBox,
  ObjectStatus,
  Icon,
  BusyIndicator,
  MessageStrip,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  TextArea
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

const requestTypeConfig: Record<DataSubjectRequestType, { label: string }> = {
  [DataSubjectRequestType.ACCESS]: { label: 'Auskunft (Art. 15)' },
  [DataSubjectRequestType.RECTIFICATION]: { label: 'Berichtigung (Art. 16)' },
  [DataSubjectRequestType.ERASURE]: { label: 'Löschung (Art. 17)' },
  [DataSubjectRequestType.RESTRICTION]: { label: 'Einschränkung (Art. 18)' },
  [DataSubjectRequestType.PORTABILITY]: { label: 'Datenübertragbarkeit (Art. 20)' },
  [DataSubjectRequestType.OBJECTION]: { label: 'Widerspruch (Art. 21)' }
};

const statusConfig: Record<DataSubjectRequestStatus, { label: string; state: string }> = {
  [DataSubjectRequestStatus.NEW]: { label: 'Neu', state: 'Information' },
  [DataSubjectRequestStatus.IDENTITY_CHECK]: { label: 'Identitätsprüfung', state: 'Warning' },
  [DataSubjectRequestStatus.IN_PROGRESS]: { label: 'In Bearbeitung', state: 'Information' },
  [DataSubjectRequestStatus.QUERY_OPEN]: { label: 'Rückfrage offen', state: 'Warning' },
  [DataSubjectRequestStatus.APPROVAL]: { label: 'Freigabe', state: 'Information' },
  [DataSubjectRequestStatus.COMPLETED]: { label: 'Erledigt', state: 'Success' },
  [DataSubjectRequestStatus.REJECTED]: { label: 'Abgelehnt', state: 'Error' }
};

const channelConfig: Record<DataSubjectRequestChannel, { label: string }> = {
  [DataSubjectRequestChannel.EMAIL]: { label: 'E-Mail' },
  [DataSubjectRequestChannel.LETTER]: { label: 'Brief' },
  [DataSubjectRequestChannel.PHONE]: { label: 'Telefon' },
  [DataSubjectRequestChannel.IN_PERSON]: { label: 'Persönlich' },
  [DataSubjectRequestChannel.WEB_FORM]: { label: 'Webformular' },
  [DataSubjectRequestChannel.OTHER]: { label: 'Sonstiges' }
};

const identityStatusConfig: Record<IdentityVerificationStatus, { label: string; state: string }> = {
  [IdentityVerificationStatus.PENDING]: { label: 'Ausstehend', state: 'Warning' },
  [IdentityVerificationStatus.VERIFIED]: { label: 'Verifiziert', state: 'Success' },
  [IdentityVerificationStatus.FAILED]: { label: 'Fehlgeschlagen', state: 'Error' },
  [IdentityVerificationStatus.NOT_REQUIRED]: { label: 'Nicht erforderlich', state: 'None' }
};

const processingStatusConfig: Record<ProcessingStatus, { label: string; state: string }> = {
  [ProcessingStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [ProcessingStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [ProcessingStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [ProcessingStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

const riskConfig: Record<RiskLevel, { label: string; state: string }> = {
  [RiskLevel.LOW]: { label: 'Niedrig', state: 'Success' },
  [RiskLevel.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [RiskLevel.HIGH]: { label: 'Hoch', state: 'Error' }
};

// Valid next statuses for each status
const validTransitions: Record<DataSubjectRequestStatus, DataSubjectRequestStatus[]> = {
  [DataSubjectRequestStatus.NEW]: [DataSubjectRequestStatus.IDENTITY_CHECK, DataSubjectRequestStatus.IN_PROGRESS, DataSubjectRequestStatus.REJECTED],
  [DataSubjectRequestStatus.IDENTITY_CHECK]: [DataSubjectRequestStatus.IN_PROGRESS, DataSubjectRequestStatus.REJECTED],
  [DataSubjectRequestStatus.IN_PROGRESS]: [DataSubjectRequestStatus.QUERY_OPEN, DataSubjectRequestStatus.APPROVAL, DataSubjectRequestStatus.COMPLETED, DataSubjectRequestStatus.REJECTED],
  [DataSubjectRequestStatus.QUERY_OPEN]: [DataSubjectRequestStatus.IN_PROGRESS, DataSubjectRequestStatus.REJECTED],
  [DataSubjectRequestStatus.APPROVAL]: [DataSubjectRequestStatus.COMPLETED, DataSubjectRequestStatus.IN_PROGRESS, DataSubjectRequestStatus.REJECTED],
  [DataSubjectRequestStatus.COMPLETED]: [],
  [DataSubjectRequestStatus.REJECTED]: []
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', padding: '0.5rem 0', borderBottom: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
    <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem', width: '180px', flexShrink: 0 }}>{label}</Text>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

export default function DsrDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canEdit, canDelete } = useUserRole();

  const [dsr, setDsr] = useState<DataSubjectRequest | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [linkedActivities, setLinkedActivities] = useState<ProcessingActivity[]>([]);
  const [allActivities, setAllActivities] = useState<ProcessingActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [editingResponse, setEditingResponse] = useState(false);
  const [responseText, setResponseText] = useState('');

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    const orgsResponse = await organizationApi.getAll();
    if (orgsResponse.success && orgsResponse.data && orgsResponse.data.length > 0) {
      const org = orgsResponse.data[0];
      setCurrentOrg(org);
      if (id) {
        const [dsrRes, linkedRes, allRes] = await Promise.all([
          dsrApi.getById(parseInt(id), org.id),
          dsrApi.getLinkedActivities(parseInt(id), org.id),
          processingApi.getByOrganization(org.id)
        ]);
        if (dsrRes.success && dsrRes.data) {
          setDsr(dsrRes.data);
          setResponseText(dsrRes.data.response_text || '');
        } else {
          navigate('/data-subject-requests');
        }
        if (linkedRes.success && linkedRes.data) setLinkedActivities(linkedRes.data);
        if (allRes.success && allRes.data) setAllActivities(allRes.data);
      }
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!dsr || !currentOrg) return;
    const response = await dsrApi.delete(dsr.id, currentOrg.id);
    if (response.success) {
      navigate('/data-subject-requests');
    }
  };

  const handleLink = async (activityId: number) => {
    if (!dsr || !currentOrg) return;
    await dsrApi.linkActivity(dsr.id, activityId, currentOrg.id);
    loadData();
    setShowLinkDialog(false);
  };

  const handleUnlink = async (activityId: number) => {
    if (!dsr || !currentOrg) return;
    await dsrApi.unlinkActivity(dsr.id, activityId, currentOrg.id);
    loadData();
  };

  const handleStatusChange = async (newStatus: DataSubjectRequestStatus) => {
    if (!dsr || !currentOrg) return;
    const response = await dsrApi.update(dsr.id, currentOrg.id, { status: newStatus });
    if (response.success && response.data) {
      setDsr(response.data);
    }
  };

  const handleSaveResponse = async () => {
    if (!dsr || !currentOrg) return;
    const response = await dsrApi.update(dsr.id, currentOrg.id, { response_text: responseText });
    if (response.success && response.data) {
      setDsr(response.data);
      setEditingResponse(false);
    }
  };

  if (loading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  if (!dsr) return null;

  const linkedIds = new Set(linkedActivities.map(a => a.id));
  const unlinkableActivities = allActivities.filter(a => !linkedIds.has(a.id));

  // Deadline warning
  const deadline = dsr.extended_deadline_at || dsr.deadline_at;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffMs = deadlineDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const isTerminal = dsr.status === DataSubjectRequestStatus.COMPLETED || dsr.status === DataSubjectRequestStatus.REJECTED;
  const isOverdue = diffDays < 0 && !isTerminal;
  const isUrgent = diffDays >= 0 && diffDays <= 5 && !isTerminal;

  const nextStatuses = validTransitions[dsr.status] || [];

  return (
    <div className="page-layout--detail">
      {/* Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Start" style={{ marginBottom: '1.5rem' }}>
        <div>
          <FlexBox alignItems="Center" style={{ gap: '0.75rem', marginBottom: '0.25rem' }}>
            <Button icon="nav-back" design="Transparent" onClick={() => navigate('/data-subject-requests')} tooltip="Zurück zur Liste" />
            <Title level="H3">{dsr.subject_name}</Title>
            <ObjectStatus state={statusConfig[dsr.status]?.state as any} showDefaultIcon>
              {statusConfig[dsr.status]?.label}
            </ObjectStatus>
          </FlexBox>
          <Text style={{ color: 'var(--sapNeutralTextColor)', marginLeft: '2.75rem' }}>
            {requestTypeConfig[dsr.request_type]?.label} — Frist: {deadlineDate.toLocaleDateString('de-DE')}
            {!isTerminal && (isOverdue
              ? ` (${Math.abs(diffDays)} Tage überfällig)`
              : ` (noch ${diffDays} Tage)`)}
          </Text>
        </div>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('data-subject-requests/' + id, currentOrg.id)}>PDF</Button>
          {canEdit && (
            <Button icon="edit" design="Default" onClick={() => navigate(`/data-subject-requests/${id}/edit`)}>
              Bearbeiten
            </Button>
          )}
          {canDelete && (
            <Button icon="delete" design="Negative" onClick={() => setShowDeleteDialog(true)}>
              Löschen
            </Button>
          )}
        </FlexBox>
      </FlexBox>

      {/* Deadline Warning Banner */}
      {isOverdue && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          Frist überschritten! Die Anfrage hätte bis zum {deadlineDate.toLocaleDateString('de-DE')} bearbeitet werden müssen ({Math.abs(diffDays)} Tage überfällig).
        </MessageStrip>
      )}
      {isUrgent && (
        <MessageStrip design="Warning" style={{ marginBottom: '1rem' }}>
          Achtung: Die Frist läuft in {diffDays} Tagen ab ({deadlineDate.toLocaleDateString('de-DE')}).
        </MessageStrip>
      )}

      {/* Tabs */}
      <TabContainer onTabSelect={() => {}} style={{ marginBottom: '1.5rem' }}>
        <Tab text="Überblick" icon="hint" selected>
          <div style={{ padding: '1rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              {/* Request Info */}
              <Card style={{ flex: '1 1 500px' }} header={<CardHeader titleText="Anfrage-Informationen" avatar={<Icon name="request" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Anfragetyp"><Text>{requestTypeConfig[dsr.request_type]?.label}</Text></InfoRow>
                  <InfoRow label="Eingangskanal"><Text>{channelConfig[dsr.channel]?.label}</Text></InfoRow>
                  <InfoRow label="Eingangsdatum"><Text>{new Date(dsr.received_at).toLocaleDateString('de-DE')}</Text></InfoRow>
                  <InfoRow label="Frist">
                    <Text style={{ color: isOverdue ? 'var(--sapNegativeColor)' : isUrgent ? 'var(--sapCriticalColor)' : undefined, fontWeight: (isOverdue || isUrgent) ? '600' : undefined }}>
                      {deadlineDate.toLocaleDateString('de-DE')}
                      {dsr.extended_deadline_at && ' (verlängert)'}
                    </Text>
                  </InfoRow>
                  <InfoRow label="Status">
                    <ObjectStatus state={statusConfig[dsr.status]?.state as any} showDefaultIcon>
                      {statusConfig[dsr.status]?.label}
                    </ObjectStatus>
                  </InfoRow>
                  <InfoRow label="Identitätsprüfung">
                    <ObjectStatus state={identityStatusConfig[dsr.identity_status]?.state as any}>
                      {identityStatusConfig[dsr.identity_status]?.label}
                    </ObjectStatus>
                  </InfoRow>
                  <InfoRow label="Abgeschlossen am"><Text>{dsr.completed_at ? new Date(dsr.completed_at).toLocaleDateString('de-DE') : '-'}</Text></InfoRow>

                  {/* Status Change Actions */}
                  {canEdit && nextStatuses.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <Text style={{ fontWeight: '600', fontSize: '0.8125rem', display: 'block', marginBottom: '0.5rem' }}>Status ändern:</Text>
                      <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                        {nextStatuses.map(s => (
                          <Button
                            key={s}
                            design="Default"
                            onClick={() => handleStatusChange(s)}
                            style={{ fontSize: '0.8125rem' }}
                          >
                            {statusConfig[s]?.label}
                          </Button>
                        ))}
                      </FlexBox>
                    </div>
                  )}
                </div>
              </Card>

              {/* Subject Info */}
              <Card style={{ flex: '1 1 300px' }} header={<CardHeader titleText="Betroffene Person" avatar={<Icon name="person-placeholder" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Name"><Text>{dsr.subject_name}</Text></InfoRow>
                  <InfoRow label="E-Mail"><Text>{dsr.subject_email || '-'}</Text></InfoRow>
                  <InfoRow label="Telefon"><Text>{dsr.subject_phone || '-'}</Text></InfoRow>
                  <InfoRow label="Referenz"><Text>{dsr.subject_reference || '-'}</Text></InfoRow>
                </div>
              </Card>
            </FlexBox>
          </div>
        </Tab>

        <Tab text="Bearbeitung" icon="edit">
          <div style={{ padding: '1rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              <Card style={{ flex: '1 1 100%' }} header={<CardHeader titleText="Beschreibung & Notizen" avatar={<Icon name="notes" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Beschreibung"><Text>{dsr.description || '-'}</Text></InfoRow>
                  <InfoRow label="Interne Notizen"><Text>{dsr.internal_notes || '-'}</Text></InfoRow>
                  <InfoRow label="Ablehnungsgrund"><Text>{dsr.rejection_reason || '-'}</Text></InfoRow>

                  <div style={{ marginTop: '1rem' }}>
                    <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '0.5rem' }}>
                      <Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Antworttext an Betroffenen</Text>
                      {!editingResponse && canEdit && (
                        <Button icon="edit" design="Transparent" onClick={() => setEditingResponse(true)}>
                          Bearbeiten
                        </Button>
                      )}
                    </FlexBox>
                    {editingResponse ? (
                      <div>
                        <TextArea
                          value={responseText}
                          onInput={(e: any) => setResponseText(e.target.value)}
                          rows={5}
                          style={{ width: '100%', marginBottom: '0.5rem' }}
                          placeholder="Antwort an die betroffene Person verfassen..."
                        />
                        <FlexBox style={{ gap: '0.5rem' }}>
                          <Button design="Emphasized" icon="save" onClick={handleSaveResponse}>Speichern</Button>
                          <Button design="Transparent" onClick={() => { setEditingResponse(false); setResponseText(dsr.response_text || ''); }}>Abbrechen</Button>
                        </FlexBox>
                      </div>
                    ) : (
                      <Text style={{ display: 'block', padding: '0.5rem', background: 'var(--sapNeutralBackground, #f5f6f7)', borderRadius: '0.25rem', minHeight: '2rem' }}>
                        {dsr.response_text || '-'}
                      </Text>
                    )}
                  </div>
                </div>
              </Card>
            </FlexBox>
          </div>
        </Tab>

        <Tab text={`Verarbeitungen (${linkedActivities.length})`} icon="documents">
          <div style={{ padding: '1rem 0' }}>
            <Card
              header={
                <CardHeader
                  titleText={`${linkedActivities.length} verknüpfte Verarbeitungen`}
                  avatar={<Icon name="documents" />}
                  action={
                    <Button icon="add" design="Transparent" onClick={() => setShowLinkDialog(true)}>
                      Verknüpfen
                    </Button>
                  }
                />
              }
            >
              {linkedActivities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Icon name="documents" style={{ fontSize: '2rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '0.5rem' }} />
                  <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
                    Noch keine Verarbeitungen verknüpft.
                  </Text>
                  <div style={{ marginTop: '0.75rem' }}>
                    <Button icon="add" design="Default" onClick={() => setShowLinkDialog(true)}>
                      Verarbeitung verknüpfen
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '0 1rem 1rem' }}>
                  <Table
                    style={{ width: '100%' }}
                    columns={
                      <>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Verarbeitung</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                        <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Risiko</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Aktion</Text></TableColumn>
                      </>
                    }
                  >
                    {linkedActivities.map(activity => (
                      <TableRow key={activity.id}>
                        <TableCell>
                          <div>
                            <Text
                              style={{ fontWeight: '600', cursor: 'pointer', color: 'var(--sapLinkColor)' }}
                              onClick={() => navigate(`/processing/${activity.id}`)}
                            >
                              {activity.name}
                            </Text>
                            <Text style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                              {activity.department}
                            </Text>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ObjectStatus state={processingStatusConfig[activity.status]?.state as any} showDefaultIcon>
                            {processingStatusConfig[activity.status]?.label}
                          </ObjectStatus>
                        </TableCell>
                        <TableCell>
                          {activity.risk_level ? (
                            <ObjectStatus state={riskConfig[activity.risk_level]?.state as any}>
                              {riskConfig[activity.risk_level]?.label}
                            </ObjectStatus>
                          ) : (
                            <Text style={{ color: 'var(--sapNeutralTextColor)' }}>-</Text>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            icon="disconnect"
                            design="Transparent"
                            onClick={() => handleUnlink(activity.id)}
                            tooltip="Verknüpfung entfernen"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Table>
                </div>
              )}
            </Card>
          </div>
        </Tab>
      </TabContainer>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <MessageBox
          type="Confirm"
          open={showDeleteDialog}
          onClose={(e: any) => {
            if (e.detail.action === 'OK') handleDelete();
            setShowDeleteDialog(false);
          }}
          titleText="Anfrage löschen"
        >
          Möchten Sie die Anfrage von „{dsr.subject_name}" wirklich löschen? Alle Verknüpfungen zu Verarbeitungen werden ebenfalls entfernt.
        </MessageBox>
      )}

      {/* Link Activity Dialog */}
      {showLinkDialog && (
        <MessageBox
          type="Confirm"
          open={showLinkDialog}
          onClose={() => setShowLinkDialog(false)}
          titleText="Verarbeitung verknüpfen"
        >
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {unlinkableActivities.length === 0 ? (
              <Text>Alle Verarbeitungen sind bereits verknüpft.</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {unlinkableActivities.map(activity => (
                  <Button
                    key={activity.id}
                    design="Default"
                    onClick={() => handleLink(activity.id)}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {activity.name} ({activity.department})
                  </Button>
                ))}
              </div>
            )}
          </div>
        </MessageBox>
      )}
    </div>
  );
}
